import {
  parseCanonicalCognitoGroup,
  parseCanonicalReviewGroup,
  YSTNJ_COGNITO_GROUP,
  YSTNJ_REVIEW_GROUP,
  type YstnjCognitoGroup,
  type YstnjReviewGroup,
  normalizeYstnjVocabularyToken,
} from './ystnjCognitoGroupVocabulary';

export const YSTNJ_MIGRATION_ONLY_COGNITO_GROUP_ALIASES = {
  administrator: YSTNJ_COGNITO_GROUP.ADMIN,
  'admissions review': YSTNJ_COGNITO_GROUP.ADMISSIONS_REVIEW,
  admissions_review: YSTNJ_COGNITO_GROUP.ADMISSIONS_REVIEW,
  'financial aid review': YSTNJ_COGNITO_GROUP.FINANCIAL_AID_REVIEW,
  financial_aid_review: YSTNJ_COGNITO_GROUP.FINANCIAL_AID_REVIEW,
} as const;

export const YSTNJ_MIGRATION_ONLY_REVIEW_GROUP_ALIASES = {
  'admissions review': YSTNJ_REVIEW_GROUP.ADMISSIONS,
  admissions_review: YSTNJ_REVIEW_GROUP.ADMISSIONS,
  'financial aid review': YSTNJ_REVIEW_GROUP.FINANCIAL_AID,
  financial_aid_review: YSTNJ_REVIEW_GROUP.FINANCIAL_AID,
} as const;

const legacyToken = (...parts: string[]): string => parts.join('');

export const YSTNJ_FORBIDDEN_LEGACY_COGNITO_GROUPS = [
  legacyToken('super', '_', 'admin'),
  legacyToken('super', '-', 'admin'),
  legacyToken('super', 'admin'),
  legacyToken('super', '_', 'administrator'),
  legacyToken('intellio', '_', 'super', '_', 'admin'),
] as const;

export type YstnjForbiddenLegacyCognitoGroup =
  (typeof YSTNJ_FORBIDDEN_LEGACY_COGNITO_GROUPS)[number];

export const isForbiddenLegacyCognitoGroup = (
  value: unknown,
): value is YstnjForbiddenLegacyCognitoGroup =>
  YSTNJ_FORBIDDEN_LEGACY_COGNITO_GROUPS.includes(
    normalizeYstnjVocabularyToken(value) as YstnjForbiddenLegacyCognitoGroup,
  );

export const parseCanonicalOrMigrationCognitoGroup = (
  value: unknown,
): YstnjCognitoGroup | null => {
  const canonical = parseCanonicalCognitoGroup(value);
  if (canonical) {
    return canonical;
  }
  const normalized = normalizeYstnjVocabularyToken(value);
  return (
    YSTNJ_MIGRATION_ONLY_COGNITO_GROUP_ALIASES as Record<string, YstnjCognitoGroup>
  )[normalized] ?? null;
};

export const parseCanonicalOrMigrationReviewGroup = (
  value: unknown,
): YstnjReviewGroup | null => {
  const canonical = parseCanonicalReviewGroup(value);
  if (canonical) {
    return canonical;
  }
  const normalized = normalizeYstnjVocabularyToken(value);
  return (
    YSTNJ_MIGRATION_ONLY_REVIEW_GROUP_ALIASES as Record<string, YstnjReviewGroup>
  )[normalized] ?? null;
};

export const normalizeCanonicalOrMigrationCognitoGroups = (
  groups: readonly unknown[] | null | undefined,
): YstnjCognitoGroup[] => [
  ...new Set(
    (groups ?? []).map((group) => parseCanonicalOrMigrationCognitoGroup(group)).filter(Boolean),
  ),
] as YstnjCognitoGroup[];
